import { Reward } from "./types";
import { asApr, conjoin } from "@silo/utils";
import { classNames } from "../../utils/class-names";
import { SuperfestIcon } from "../../icons";
import { Percent } from "@silo/types";
import { GLOBAL_CONFIG } from "@silo/config";

interface DepositAprBreakdownProps {
  depositApr: bigint;
  isProtected: boolean;
  hideProtectedMessage?: boolean;
  rewards: Reward[];
  revenueApr: Percent;
}

export function DepositAprBreakdown({
  depositApr,
  isProtected,
  hideProtectedMessage = false,
  rewards,
  revenueApr,
}: DepositAprBreakdownProps): JSX.Element {
  const showProtectedMessage = !hideProtectedMessage;
  const hasRewards = rewards.length > 0;
  const hasProtectedRevenue = isProtected && revenueApr > 0n;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <DepositBaseApr depositApr={depositApr} isProtected={isProtected} />

        {hasProtectedRevenue && (
          <div className="flex items-center gap-2.5 text-shale-200">
            <span className="font-mono font-medium text-white">
              {asApr(revenueApr)}
            </span>

            <span>
              revenue APR in {GLOBAL_CONFIG.revenue.rewardTokenSymbol}
            </span>
          </div>
        )}

        {rewards.map((reward, i) => (
          <DepositRewardApr key={i} reward={reward} />
        ))}
      </div>

      {hasRewards && showProtectedMessage && isProtected && (
        <div className="p-2.5 rounded-lg bg-amber-300/10 text-amber-300">
          Depositors in this market earn rewards APR in{" "}
          {conjoin(rewards.map((reward) => reward.symbol))} on a non-borrowable
          (protected) deposit.
        </div>
      )}
    </div>
  );
}

interface DepositBaseAprProps {
  depositApr: bigint;
  isProtected: boolean;
  className?: string;
}

export function DepositBaseApr({
  depositApr,
  isProtected,
  className,
}: DepositBaseAprProps) {
  return (
    <div
      className={classNames(
        "flex items-center gap-2.5 text-shale-200",
        className
      )}
    >
      <span className="font-mono font-medium text-white">
        {isProtected ? "--" : asApr(depositApr)}
      </span>

      <span>deposit APR</span>
    </div>
  );
}

interface DepositRewardAprProps {
  className?: string;
  reward: Reward;
}

export function DepositRewardApr({
  className,
  reward,
}: DepositRewardAprProps): JSX.Element {
  const { symbol, apr, isSuperfest } = reward;

  return (
    <div
      className={classNames(
        "flex items-center gap-2.5 text-shale-200",
        className
      )}
    >
      <span className="font-mono font-medium text-white">{asApr(apr)}</span>

      {isSuperfest ? (
        <>
          <SuperfestIcon className="w-4 h-4" />
          <span>SuperFest rewards</span>
        </>
      ) : (
        <span>rewards APR in {symbol}</span>
      )}
    </div>
  );
}
